import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Scale, FileText, Mic, MessageSquare, Send } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';

const ReportDetailPage = () => {
  const { reportId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const isCoach = user?.role === 'coach';

  useEffect(() => {
    apiFetch(`/api/reports/${reportId}`)
      .then((r) => r.ok ? r.json() : null)
      .then(setReport)
      .catch(() => setReport(null))
      .finally(() => setLoading(false));
  }, [reportId]);

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) return <div className="loading"><div className="loading-spinner" /></div>;

  if (!report) return (
    <div className="page-container">
      <button className="btn btn-secondary mb-4" style={{ gap: '6px' }} onClick={() => navigate(-1)}>
        <ArrowLeft size={16} /> {t('common.back')}
      </button>
      <p style={{ color: 'var(--muted-foreground)' }}>{t('reports.not_found')}</p>
    </div>
  );

  return (
    <div className="page-container animate-fade-in">
      {/* Header */}
      <div className="header" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            className="btn btn-icon btn-secondary"
            onClick={() => navigate(-1)}
            style={{ flexShrink: 0 }}
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            {isCoach && report.athlete_name && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                {report.athlete_picture
                  ? <img src={report.athlete_picture} alt="" style={{ width: '22px', height: '22px', borderRadius: '50%', objectFit: 'cover' }} />
                  : <span style={{ fontSize: '16px' }}>🐉</span>
                }
                <p style={{ fontSize: '12px', color: 'var(--muted-foreground)' }}>{report.athlete_name}</p>
              </div>
            )}
            <h1 className="header-title">{t('reports.detail_title')}</h1>
            <p style={{ fontSize: '12px', color: 'var(--muted-foreground)', marginTop: '2px' }}>{formatDate(report.created_at)}</p>
          </div>
        </div>
        <img src="/dragon-logo.png" alt="DragonFit" className="header-logo" />
      </div>

      {/* Peso */}
      {report.weight != null && (
        <div className="card mb-3" style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <Scale size={22} style={{ color: 'var(--primary)', flexShrink: 0 }} />
          <div>
            <p style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted-foreground)', marginBottom: '2px' }}>
              {t('reports.weight_label')}
            </p>
            <p style={{ fontSize: '24px', fontWeight: '700', color: 'var(--foreground)', lineHeight: 1 }}>
              {report.weight}
              <span style={{ fontSize: '14px', fontWeight: '400', marginLeft: '4px' }}>kg</span>
            </p>
          </div>
        </div>
      )}

      {/* Fotos */}
      {report.photos?.length > 0 && (
        <div className="card mb-3">
          <p style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted-foreground)', marginBottom: '12px' }}>
            {t('reports.photos_label')}
          </p>
          <div className="report-photos-grid">
            {report.photos.map((src, i) => (
              <div
                key={i}
                className="report-photo-thumb"
                onClick={() => setSelectedPhoto(src)}
                style={{ cursor: 'zoom-in' }}
              >
                <img src={src} alt={`photo-${i}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notas */}
      {report.notes && (
        <div className="card mb-3">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <FileText size={16} style={{ color: 'var(--primary)' }} />
            <p style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted-foreground)' }}>
              {t('reports.notes_label')}
            </p>
          </div>
          <p style={{ fontSize: '15px', color: 'var(--foreground)', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>
            {report.notes}
          </p>
        </div>
      )}

      {/* Audio del reporte */}
      {report.audio && (
        <div className="card mb-3">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <Mic size={16} style={{ color: 'var(--primary)' }} />
            <p style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted-foreground)' }}>
              {t('reports.audio_label')}
            </p>
          </div>
          <audio src={report.audio} controls style={{ width: '100%' }} />
        </div>
      )}

      {/* Feedback */}
      {report.feedback ? (
        <>
          <div className="card mb-3" style={{
            background: 'linear-gradient(135deg, rgba(var(--primary-rgb),0.10) 0%, rgba(var(--primary-rgb),0.03) 100%)',
            border: '1px solid rgba(var(--primary-rgb),0.3)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <MessageSquare size={16} style={{ color: 'var(--primary)' }} />
              <p style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--primary)' }}>
                {t('reports.feedback_section')}
              </p>
              <span style={{ fontSize: '12px', color: 'var(--muted-foreground)', marginLeft: 'auto' }}>
                {formatDate(report.feedback.sent_at)}
              </span>
            </div>
            {report.feedback.text && (
              <p style={{
                fontSize: '15px', color: 'var(--foreground)', lineHeight: '1.7',
                whiteSpace: 'pre-wrap', marginBottom: report.feedback.audio ? '14px' : 0,
              }}>
                {report.feedback.text}
              </p>
            )}
            {report.feedback.audio && (
              <audio src={report.feedback.audio} controls style={{ width: '100%' }} />
            )}
          </div>

          {isCoach && (
            <button
              className="btn btn-outline"
              style={{ width: '100%', marginBottom: '16px' }}
              onClick={() => navigate(`/reports/${reportId}/feedback`)}
            >
              <Send size={16} /> {t('reports.update_feedback_btn')}
            </button>
          )}
        </>
      ) : isCoach ? (
        <div className="card mb-3" style={{ border: '1px dashed rgba(var(--primary-rgb),0.35)', background: 'transparent', textAlign: 'center', padding: '28px 16px' }}>
          <p style={{ fontSize: '14px', color: 'var(--muted-foreground)', marginBottom: '16px' }}>
            {t('reports.no_feedback_yet')}
          </p>
          <button
            className="btn btn-primary"
            style={{ width: '100%' }}
            onClick={() => navigate(`/reports/${reportId}/feedback`)}
          >
            <Send size={16} /> {t('reports.send_feedback_btn')}
          </button>
        </div>
      ) : (
        <div className="card mb-3" style={{ border: '1px dashed var(--border)', background: 'transparent', textAlign: 'center', padding: '24px 16px' }}>
          <p style={{ fontSize: '14px', color: 'var(--muted-foreground)' }}>
            {t('reports.waiting_feedback')}
          </p>
        </div>
      )}

      {/* Lightbox de fotos */}
      {selectedPhoto && (
        <div
          className="modal-overlay"
          onClick={() => setSelectedPhoto(null)}
          style={{ zIndex: 9999, cursor: 'zoom-out' }}
        >
          <img
            src={selectedPhoto}
            alt="full"
            style={{ maxWidth: '95vw', maxHeight: '90vh', borderRadius: '8px', objectFit: 'contain' }}
          />
        </div>
      )}
    </div>
  );
};

export default ReportDetailPage;
